import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { CancellationAccessConfig } from '@acorn-portal/shared/src/pages/policy/types';
import { useMemo } from 'react';
import { useCancellationsPreRequisites } from '../use-cancellation-pre-requisites';
import { getCancellationsAccessMessageConfig } from './get-cancellation-access-message-config';
import { getCancellationBaseAccess } from './get-cancellation-base-access';

/**
 * Hook to determine the access configuration for the cancellations journey.
 *
 *  @returns Configuration object containing:
 *   - `baseAccess`: Used to show/hide points of journey access e.g. Dashboard > Cancel Policy button
 *   - `fullAccess`: Used to safeguard routes - derived from both base access and backend verification (Cancellations API - isEligible)
 *   - `accessNotification`: Object with `show` flag and `reasonBlocked` message for displaying ineligibility info to the user
 *
 */
export const useCancellationAccessConfig = (): CancellationAccessConfig => {
	// Selectors
	const cancellationState = useAppSelector((state) => state.cancellation);
	const { isPreEligible, preEligibilityStatus, isEligible, ineligibilityReason } = cancellationState;

	// ** Base access **
	// Determined by the combination of basic pre-requisites (FE driven) and pre-eligibility (BE driven)
	const preRequisitesPass = useCancellationsPreRequisites();
	const skipPreEligibility = !!preEligibilityStatus?.error;
	const baseAccess = useMemo(
		() => getCancellationBaseAccess(preRequisitesPass, skipPreEligibility, isPreEligible),
		[preRequisitesPass, isPreEligible, skipPreEligibility],
	);

	// ** Full access **
	// Derived from base access (above) and eligibility state (BE driven)
	const fullAccess = useMemo(() => baseAccess && isEligible, [baseAccess, isEligible]);

	// Message configuration for displaying access information
	const accessMessageConfig = useMemo(
		() => getCancellationsAccessMessageConfig(baseAccess, fullAccess, ineligibilityReason),
		[baseAccess, fullAccess, ineligibilityReason],
	);

	// Access configuration object
	const accessConfig: CancellationAccessConfig = useMemo(
		() => ({
			baseAccess,
			fullAccess,
			accessMessageConfig,
		}),
		[baseAccess, fullAccess, accessMessageConfig],
	);

	return accessConfig;
};
